import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { softwareAppSchema } from '../../Components/SEO';
import SEOPageLayout from '../../Components/SEOPageLayout';

export default function RemoteDesktopPage() {
  return (
    <SEOPageLayout>
      <SEO
        title="Browser-Based Remote Desktop — Zero Install, Sub-100ms Latency | CheckScript"
        description="Experience flawless remote access with CheckScript Remote. Powered by Ultra-low latency streaming protocol and Cloudflare edge network for ultra-low latency, browser-based remote desktop."
        keywords="browser remote desktop, Ultra-low latency streaming protocol remote desktop, low latency remote access, remote desktop no install, CheckScript Remote"
        path="/features/remote-desktop"
        structuredData={softwareAppSchema()}
      />

      <section className="seo-hero seo-animate">
        <div className="seo-mesh-bg"></div>
        <div className="seo-container seo-container--sm">
          <div className="seo-hero-badge">🖥️ CheckScript Remote</div>
          <h1 className="seo-h1">
            Ultra-Low Latency <span className="seo-gradient-text">Browser Remote Desktop</span>
          </h1>
          <p className="seo-lead">
            Access any Windows machine directly from your web browser. Zero viewer installation required. Powered by modern Ultra-low latency streaming protocol infrastructure for sub-100ms latency and pristine H.264 video quality.
          </p>
          <div className="seo-hero-actions">
            <Link to="/download" className="seo-btn seo-btn--primary">Get the Host Client</Link>
            <Link to="/pricing" className="seo-btn seo-btn--secondary">View Support Plans</Link>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container">
          <div className="seo-bento-grid" style={{ alignItems: 'center', gap: '48px' }}>
            <div>
              <h2 className="seo-h2">Next-Generation Architecture</h2>
              <p className="seo-mb-24 seo-text-secondary">
                Traditional remote desktop protocols are bloated and slow. CheckScript Remote is built on Ultra-low latency streaming protocol, the same technology that powers modern video conferencing, optimized for screen sharing and remote control.
              </p>
              <ul className="seo-feature-list">
                <li><strong>Cloudflare Edge Network:</strong> We utilize Cloudflare Calls Global routing network, routing your connection through the closest data center globally for minimal latency.</li>
                <li><strong>Hardware H.264 Encoding:</strong> The host client leverages your GPU (via Hardware-accelerated capture/Hardware-accelerated capture) to encode the screen at 30+ FPS without draining CPU resources.</li>
                <li><strong>Peer-to-Peer Fallback:</strong> Whenever possible, connections are established P2P, completely bypassing intermediary servers for true zero-lag performance.</li>
                <li><strong>End-to-End Encryption:</strong> Every frame and keystroke is secured with 256-bit Ultra-low latency streaming protocol encryption.</li>
              </ul>
            </div>
            <div className="seo-glass-card" style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🌐</div>
              <h3 className="seo-h3">Browser Native</h3>
              <p className="seo-text-secondary">
                The viewer runs entirely in Chrome, Edge, or Firefox. No hefty client downloads, no admin privileges needed on the viewing machine. Just share a link and connect.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-section seo-animate">
        <div className="seo-container seo-container--sm">
          <h2 className="seo-h2 seo-text-center seo-mb-48">Latency Comparison</h2>
          <div className="seo-table-wrap">
            <table className="seo-table">
              <thead>
                <tr>
                  <th>Protocol / Service</th>
                  <th>Architecture</th>
                  <th className="seo-highlight-col">Average Latency</th>
                  <th>Browser Support</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Traditional VNC</td>
                  <td>TCP Relay</td>
                  <td>250ms - 500ms</td>
                  <td><span className="seo-cross">✗</span> Requires Client</td>
                </tr>
                <tr>
                  <td>Standard RDP</td>
                  <td>TCP/Ultra-low latency streaming protocol</td>
                  <td>150ms - 300ms</td>
                  <td><span className="seo-cross">✗</span> Requires Client</td>
                </tr>
                <tr>
                  <td>Legacy Browser Solutions</td>
                  <td>WebSocket Canvas</td>
                  <td>300ms+</td>
                  <td><span className="seo-check">✓</span> Yes (Poor Quality)</td>
                </tr>
                <tr>
                  <td><strong>CheckScript Remote</strong></td>
                  <td><strong>Ultra-low latency streaming protocol (Ultra-low latency streaming protocol)</strong></td>
                  <td className="seo-highlight-col"><strong>50ms - 90ms</strong></td>
                  <td><strong><span className="seo-check">✓</span> Native HTML5 Video</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container">
          <div className="seo-section-header">
            <h2 className="seo-h2">Perfect for Support & Collaboration</h2>
          </div>
          <div className="seo-bento-grid">
            <div className="seo-bento-box seo-bento-square">
              <h3>Developer Pair Programming</h3>
              <p>Share your IDE with a colleague instantly. The ultra-low latency makes remote typing feel native.</p>
            </div>
            <div className="seo-bento-box seo-bento-square">
              <h3>IT Troubleshooting</h3>
              <p>Provide immediate support to clients without walking them through complex VPN or client installations.</p>
            </div>
            <div className="seo-bento-box seo-bento-square">
              <h3>Educational Assistance</h3>
              <p>Tutors can seamlessly guide students through complex software or coding assignments in real-time.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-cta seo-animate">
        <div className="seo-container">
          <div className="seo-cta-box">
            <h2 className="seo-h2">Connect Anywhere, Instantly</h2>
            <p className="seo-text-secondary seo-mb-32">Start using the fastest browser-based remote desktop solution available.</p>
            <Link to="/download" className="seo-btn seo-btn--primary">Download Host Software</Link>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
